// Team Pace Board — the VP's replacement for the self-only Performance
// Targets widget (Ali review 2026-07-07). Every AM's realized Rentalman $
// against their 6-tier ladder, side by side, with a pace column (realized vs
// Breakeven prorated by year elapsed). Leadership/admin see everyone; a
// supervisor sees their reports (server-scoped). Reps never render this —
// they keep the personal PerformanceTargetsWidget.
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtMoneyK, useIsMobile } from './UI';

const TIER_SHORT = {
  breakeven: 'BE', bogey: 'Bogey', par: 'Par',
  birdie: 'Birdie', eagle: 'Eagle', hole_in_one: 'HIO',
};

export default function TeamPaceBoard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const isMobile = useIsMobile();
  const year = new Date().getFullYear();

  useEffect(() => {
    api.teamPaceBoard(year).then(setData).catch(e => setError(e.message));
  }, [year]);

  // Fraction of the year elapsed — drives the pace math client-side so the
  // board and the tooltip always agree.
  const yearElapsed = useMemo(() => {
    const start = new Date(year, 0, 1).getTime();
    return Math.min(1, (Date.now() - start) / (365 * 86400000));
  }, [year]);

  if (error) return null; // never break the dashboard over the board
  if (!data) return null;
  if (!data.ams.length) return null;

  const rows = data.ams.map(a => {
    const tiers = a.tiers.filter(t => t.value > 0);
    const breakeven = (a.tiers.find(t => t.key === 'breakeven') || {}).value || 0;
    const top = tiers.length ? tiers[tiers.length - 1].value : 0;
    // Highest tier already attained (tiers come lowest→highest).
    let attained = null;
    for (const t of tiers) if (a.realized >= t.value) attained = t;
    // Next tier up + progress toward it.
    const next = tiers.find(t => a.realized < t.value) || null;
    const pace = breakeven > 0 ? a.realized / (breakeven * yearElapsed) : null;
    return { ...a, breakeven, top, attained, next, pace, hasLadder: tiers.length > 0 };
  });

  const paceColor = (p) => p == null ? colors.textMuted
    : p >= 1 ? colors.green : p >= 0.8 ? '#B45309' : colors.red;
  const paceLabel = (p) => p == null ? 'no target' : `${Math.round(p * 100)}%`;

  const Bar = ({ r }) => {
    const pct = r.top > 0 ? Math.min(100, Math.round((r.realized / r.top) * 100)) : 0;
    return (
      <div style={{ position: 'relative', height: 10, background: colors.graySoft, borderRadius: 5, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: 5,
          background: r.pace == null ? '#9CA3AF' : r.pace >= 1 ? colors.green : r.pace >= 0.8 ? colors.orange : colors.red,
        }} />
        {/* Breakeven marker on the ladder bar */}
        {r.top > 0 && r.breakeven > 0 && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: 2, background: '#1F2937',
            left: `${Math.min(99, (r.breakeven / r.top) * 100)}%`,
          }} />
        )}
      </div>
    );
  };

  return (
    <div style={styles.card}>
      <h2 style={{ ...styles.h2, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        🎯 Team Pace Board
        <span style={{ color: colors.textMuted, fontWeight: 400, fontSize: 13 }}>
          · {data.ams.length} AMs · {year} · {Math.round(yearElapsed * 100)}% of year elapsed
        </span>
        <span
          title={'Realized = every Rentalman invoice dollar credited to the AM (Base + TA, all branches), year to date. Pace = realized ÷ (Breakeven target × fraction of the year elapsed) — 100%+ means on track for Breakeven. The dark tick on each bar marks Breakeven; the bar runs to the top tier (Hole in One).'}
          style={{ cursor: 'help', color: colors.textMuted, fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: '50%', width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >?</span>
      </h2>
      {isMobile ? (
        <div>
          {rows.map(r => (
            <div key={r.user_id} style={{ padding: '10px 0', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</span>
                <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {fmtMoneyK(r.realized)} · <strong style={{ color: paceColor(r.pace) }}>{paceLabel(r.pace)}</strong>
                </span>
              </div>
              <div style={{ margin: '5px 0 3px' }}><Bar r={r} /></div>
              <div style={{ fontSize: 11, color: colors.textMuted }}>
                {r.hasLadder
                  ? <>{r.attained ? `✓ ${r.attained.key === 'hole_in_one' ? 'Hole in One' : r.attained.key.charAt(0).toUpperCase() + r.attained.key.slice(1)}` : 'Below Breakeven'}{r.next ? ` · next: ${TIER_SHORT[r.next.key]} @ ${fmtMoneyK(r.next.value)}` : ''}</>
                  : 'No targets set — Admin → Performance Targets'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <table style={{ ...styles.table, fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={styles.th}>Account Mgr</th>
              <th style={styles.th}>Branch</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Realized YTD</th>
              <th style={{ ...styles.th, width: '28%' }}>Ladder</th>
              <th style={styles.th}>Tier</th>
              <th style={{ ...styles.th, textAlign: 'right' }} title="Realized ÷ (Breakeven × year elapsed)">Pace</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Next Tier</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.user_id}>
                <td style={{ ...styles.td, fontWeight: 600 }}>{r.name}</td>
                <td style={{ ...styles.td, color: colors.textMuted }}>{r.branch || '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700 }}>{fmtMoneyK(r.realized)}</td>
                <td style={styles.td}>{r.hasLadder ? <Bar r={r} /> : <span style={{ fontSize: 11, color: '#9CA3AF' }}>no targets set</span>}</td>
                <td style={{ ...styles.td, fontWeight: 600, color: r.attained ? colors.green : colors.textMuted }}>
                  {r.hasLadder ? (r.attained ? `✓ ${TIER_SHORT[r.attained.key]}` : '—') : ''}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontWeight: 700, color: paceColor(r.pace) }}>
                  {paceLabel(r.pace)}
                </td>
                <td style={{ ...styles.td, textAlign: 'right', fontSize: 11, color: colors.textMuted }}>
                  {r.next ? `${TIER_SHORT[r.next.key]} @ ${fmtMoneyK(r.next.value)}` : (r.hasLadder ? '🏆 topped out' : '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
